import {Box, Text} from '@mantine/core';
import Image from 'next/image';
import type React from 'react';
import {MdCheckCircle} from 'react-icons/md';
import {GSSKT_HOST} from '../../../../../config/constants/hosts';
import type {Recruitment} from '../../../../../core/repositories/recruitingInfoRepository';
import {cn} from '../../../../../lib/classname';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {GradientEntryButton} from '../../../../../shared/components/EntryButton/GradientEntryButton';
import {appendQueryParam} from '../../../../../shared/services/appendQueryParam';
import {buildRecruitmentCardGtmContent} from '../../../../../shared/services/buildRecruitmentCardGtmContent';
import {buildScheduleGtmContent} from '../../../../../shared/services/buildScheduleGtmContent';
import {Deadline} from '../../../../recruitingInfo/components/Container/Presenter/Deadline';
import {Tags} from '../../../../recruitingInfo/components/Container/Presenter/Tags';
import {getDeadlineCountdownType} from '../../../../recruitingInfo/usecase/getDeadlineCountdown';
import {ScheduleButtonNewDesign} from '../../../../recruitment/components/ScheduleButton';

type Props = {
  recruitment: Recruitment;
  componentId?: string;
  className?: string;
  hasTargetYear?: boolean;
  isViewed?: boolean;
  rank?: number;
  entryButtonLabel?: string;
  entryButtonTooltip?: string;
  entryButtonGradientClassName?: string;
  isEntered?: boolean;
  scheduleGtmLocation?: string;
};

export const CarouselRecruitmentCard: React.FC<Props> = ({
  recruitment,
  className,
  componentId,
  hasTargetYear = true,
  isViewed = false,
  rank,
  entryButtonLabel,
  entryButtonTooltip,
  entryButtonGradientClassName,
  isEntered = false,
  scheduleGtmLocation = 'top-recruitment-carousel',
}) => {
  const recruitingInfoPage = `/recruiting_info/${recruitment.category === 'archive' ? 'archives' : 'view'}/${recruitment.detailID}`;

  const firstCompany = recruitment.companies[0];
  const hasLogo = firstCompany?.logoURLs?.['140x105'];
  const canShowEntryButton =
    recruitment.entryURL &&
    ['medium', 'large'].includes(firstCompany?.pageType ?? '');

  const entryButtonUrl = recruitment.entryURL
    ? appendQueryParam(
        recruitment.entryURL,
        'source_url',
        recruitment.detailPageURL,
      )
    : '';

  const isRanking = rank !== undefined;

  const gtmContent = buildRecruitmentCardGtmContent({
    recruitmentItemId: recruitment.id,
    deadlineCountdown: getDeadlineCountdownType(
      recruitment.schedules[0]?.entryEnd,
    ),
  });

  // 1032 外資就活ドットコム
  const noLinkCompany = firstCompany?.id === 0 || firstCompany?.id === 1032;

  return (
    <Box
      className={cn(
        'bg-white rounded-16 shadow-card w-[320px] pc:w-[369px] h-[220px] p-16 pc:p-0 m-[1px]',
        className,
      )}
    >
      <Box className="h-full flex flex-col justify-between gap-4 pc:p-16">
        <Box className="flex flex-col gap-4">
          {noLinkCompany ? (
            <div className="flex items-center gap-8 h-[28px]">
              {isRanking && (
                <div className="text-[20px] pc:text-[24px] font-bold flex-shrink-0">
                  {rank}
                </div>
              )}
              {recruitment.category === 'client' && hasLogo && (
                <Image
                  className="flex h-[28px] w-[58px] items-center bg-white object-contain flex-shrink-0"
                  src={firstCompany.logoURLs['140x105']}
                  alt={recruitment.title}
                  width={58}
                  height={28}
                />
              )}
              <Text className="text-base text-black-1 pc:text-black line-clamp-1 flex-1">
                {firstCompany?.name}
              </Text>
            </div>
          ) : (
            <CommonLink
              href={`/company/${firstCompany?.id}`}
              target={'_blank noreferrer'}
              className="flex items-center gap-8 h-[28px] cursor-pointer hover:opacity-50"
            >
              {isRanking && (
                <div className="text-[20px] pc:text-[24px] font-bold flex-shrink-0">
                  {rank}
                </div>
              )}
              {recruitment.category === 'client' && hasLogo && (
                <Image
                  className="flex h-[28px] w-[58px] items-center bg-white object-contain flex-shrink-0"
                  src={firstCompany.logoURLs['140x105']}
                  alt={recruitment.title}
                  width={58}
                  height={28}
                />
              )}
              <Text className="text-base text-black-1 pc:text-black line-clamp-1 flex-1">
                {firstCompany?.name}
              </Text>
            </CommonLink>
          )}
          <Box className="text-black-2 cursor-default">
            <Tags
              tags={recruitment.tags}
              targetYear={recruitment.targetYear}
              type={recruitment.type}
              groupedJobTypes={recruitment.groupedJobTypes}
              hasTargetYear={hasTargetYear}
              isViewed={isViewed}
              size="sm"
              lineClampOne
            />
          </Box>
          <Box>
            <CommonLink
              href={recruitingInfoPage}
              data-gtm-click={componentId}
              data-gtm-content={gtmContent}
              data-gtm-click-recruitment-detail-button="true"
            >
              <Text className="text-base text-black-1 font-bold line-clamp-2 cursor-pointer hover:opacity-40 pc:hover:opacity-50 pc:break-all">
                {recruitment.title}
              </Text>
            </CommonLink>
          </Box>
        </Box>

        <Box className="flex flex-col gap-8">
          {recruitment.schedules.length > 0 && recruitment.schedules[0] && (
            <Box className="flex justify-between items-center gap-12 pc:items-start">
              <Deadline
                schedule={recruitment.schedules[0]}
                layout="text"
                withCountdownBadge
              />

              <Box className="flex-shrink-0 pc:flex pc:flex-col pc:items-center pc:gap-8">
                <ScheduleButtonNewDesign
                  scheduleId={recruitment.schedules[0].id}
                  isRegisteredInitial={
                    recruitment.schedules[0].isCalendarRegistered
                  }
                  registeredCountInitial={
                    recruitment.schedules[0].calendarRegisteredCount
                  }
                  recruitmentData={{
                    companyId: firstCompany?.id ?? 0,
                    companyName: firstCompany?.name ?? '',
                    entryTitle: recruitment.title,
                    detailURL: `${GSSKT_HOST}${recruitment.detailPageURL}`,
                  }}
                  dataGtmClick="recruitment-schedule-add"
                  dataGtmContent={buildScheduleGtmContent({
                    scheduleId: recruitment.schedules[0].id,
                    location: scheduleGtmLocation,
                  })}
                />
              </Box>
            </Box>
          )}

          <Box className="flex justify-end items-start w-full">
            <Box className="flex gap-8">
              <CommonLink
                href={recruitingInfoPage}
                data-gtm-click-recruitment-detail-button="true"
                className={cn(
                  'text-sm font-bold',
                  'flex items-center justify-center w-[83px] h-[36px] rounded-[8px] hover:opacity-80',
                  'bg-white text-black-3 border border-black-3',
                )}
                data-gtm-click={`detail-${componentId}`}
                data-gtm-content={gtmContent}
              >
                募集詳細
              </CommonLink>
              {isEntered && (
                <div className="flex h-[36px] items-center justify-center gap-4 rounded-[8px] bg-disabled-1 px-12 text-sm font-bold text-black-2">
                  <MdCheckCircle size={16} aria-hidden="true" />
                  エントリー済み
                </div>
              )}
              {!isEntered && canShowEntryButton && (
                <GradientEntryButton
                  entryUrl={entryButtonUrl}
                  customId={`recruiting_info_entry_button_${recruitment.detailID}`}
                  withShadow
                  dataGtmClick={`entry-${componentId}`}
                  dataGtmContent={gtmContent}
                  companyId={firstCompany?.id}
                  companyName={firstCompany?.name}
                  entryTitle={recruitment.title}
                  deadline={recruitment.schedules[0]?.entryEnd}
                  label={entryButtonLabel}
                  tooltip={entryButtonTooltip}
                  gradientClassName={entryButtonGradientClassName}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
